<template>
  <v-card v-if="show" class="chat-container">
    <v-card-title style="padding-bottom: 0">
      <div class="chat-header">
        <span class="truncate-title">{{ title }}</span>
        <v-btn icon size="small" variant="text" @click="closeBox">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text class="chat-body">
      <div class="chat-history">
        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['d-flex flex-row align-center my-2', msg.sender.userId === userId ? 'justify-end' : null]"
        >
          <div class="float-sender-name">
            <span v-if="msg.sender.userId === userId" class="text-white mr-3 message message-self">{{ msg.message }}</span>
            <span
              v-if="msg.sender.userId === userId && msg.metaArrays.find(meta => meta.key === 'senderUser')"
              class="sender-name mr-3"
            >
              {{ msg.metaArrays.find(meta => meta.key === "senderUser").value[0] }}
            </span>
          </div>
          <v-avatar :color="msg.sender.userId === userId ? primaryColor : secondaryColor" size="22">
            <span class="text-white" style="font-size: smaller">{{ getInitials(msg.sender.nickname) }}</span>
          </v-avatar>
          <div>
            <span v-if="msg.sender.userId !== userId" class="text-white ml-3 message message-other">{{ msg.message }}</span>
            <span v-if="msg.sender.userId !== userId && getSenderName(msg)" class="sender-name ml-3">
              {{ getSenderName(msg) }}
            </span>
          </div>
        </div>
      </div>

      <v-textarea
        v-model="msg"
        variant="outlined"
        label="Write a message..."
        rows="1"
        hide-details
        class="custom-textarea"
        @keydown.enter="handleKeyPress"
      ></v-textarea>

      <v-btn block :color="primaryColor" class="text-white" @click="sendMessage">
        Send
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script>
import chatService from "@/services/chatService";
import { mapGetters } from "vuex";

export default {
  name: "ChatDialog",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    messageThread: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      title: "",
      msg: "",
      messages: [],
      userId: null,
      primaryColor: 'primary',
      secondaryColor: 'info',
    };
  },
  methods: {
    async init() {
      this.msg = "";
      if (this.messageThread != null) {
        this.title = this.messageThread.companyNames;
        this.userId = this.messageThread.userId;
        this.messages = this.messageThread.messages;
      }
    },
    getSenderName(msg) {
      return msg.metaArrays?.find(meta => meta.key === "senderUser")?.value[0];
    },
    getInitials(name) {
      if (name === "") return "";

      return name
        .match(/\b(\w)/g)
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    closeBox() {
      this.$emit("close");
    },
    async handleKeyPress(event) {
      // Add newline on Shift+Enter otherwise send message
      if (!event.shiftKey) {
        event.preventDefault();
        await this.sendMessage();
      }
    },
    async sendMessage() {
      if (!this.msg.trim()) {
        this.$root.$snackbar.message("Please write a message before sending.");
        return;
      }

      try {
        const toParty = {
          id: this.messageThread.connectedCompanyId || 0,
          name: this.messageThread.companyNames || "",
          initials: this.messageThread.companyNames.charAt(0).toUpperCase(),
          userId: this.messageThread.userId || "",
          connectedCompanyCode: this.messageThread.connectedCompanyCode || "",
        };

        await chatService.sendMessage(toParty, this.msg, `${this.user.firstName} ${this.user.lastName}`);

        console.log("Message sent:", this.msg);
        this.msg = "";
      } catch (error) {
        console.error("Error sending message:", error);
        this.$root.$snackbar.error("Failed to send the message. Please try again.");
        this.messages.shift();
      }
    },
  },
  computed: {
    ...mapGetters("auth0Store", ["user"]),
  },
  watch: {
    messageThread() {
      this.init();
    },
  },
};
</script>

<style scoped>
.chat-container {
  position: fixed;
  bottom: 20px;
  right: 370px;
  width: 300px;
  z-index: 2000;
  max-height: 380px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.8em;
}

.chat-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-history {
  overflow-y: auto;
  max-height: 200px;
  display: flex;
  flex-direction: column-reverse;
}

.truncate-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message {
  display: block;
  border-radius: 5px;
  padding: 0 4px;
  font-size: small;
  line-height: 1.1rem;
  white-space: pre-line;
  margin-top: 5px;
}

.message-self {
  background-color: rgb(var(--v-theme-primary));
}

.message-other {
  background-color: rgb(var(--v-theme-info));
}

.custom-textarea :deep(textarea) {
  font-size: small;
  line-height: 1.1rem;
}

.float-sender-name {
  justify-items: end;
}

.sender-name {
  display: block;
  font-size: xx-small;
  font-style: italic;
  line-height: 1.2;
  margin-top: 1px;
}
</style>
